import { uniqueId } from "lodash";
import moment from "moment";
import { CSSProperties, ReactNode, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { IEsteiraDetalhe } from "../../interfaces/IEsteira";
import { ICartaoDetalhe, IConciliados, ILancamento } from "../../interfaces/IRelatorio";
import EmpresasService from "../../services/EmpresasService";
import { Formatter } from "../relatorios/Relatorio";
import cartaoNovaService from "../../services/cartaoNovaService";
import esteiraNovaService from "../../services/esteiraNovaService";
import lancamentoNovaService from "../../services/lancamentoNovaService";

function CopyField({ text, children, style }: { text: string; children: ReactNode; style?: CSSProperties }) {
    const [show, setShow] = useState(false);
    const [label, setLabel] = useState('Clique para copiar');

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
            setLabel('Copiado!');
            setTimeout(() => setLabel('Clique para copiar'), 1500);
        });
    };

    return (
        <span
            style={{ position: 'relative', cursor: 'pointer', display: 'inline-block', ...style }}
            onClick={handleClick}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && (
                <span style={{
                    position: 'absolute', bottom: 'calc(100% + 4px)', left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(33,37,41,0.88)', color: '#fff',
                    padding: '3px 8px', borderRadius: '4px',
                    fontSize: '11px', whiteSpace: 'nowrap',
                    zIndex: 9999, pointerEvents: 'none',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                }}>
                    {label}
                </span>
            )}
        </span>
    );
}

export function CardLancamento({ id, vinculado, tipo, valor, historico, data, onClick, removerClick, mostrarRotulo = false }: { id?: number, vinculado: boolean, tipo: string, valor: number, historico: string, onClick?: Function, removerClick?: Function, data: Date, mostrarRotulo: boolean }) {
    const isCredito = tipo === 'Crédito';
    const accentColor = isCredito ? '#28a745' : '#dc3545';
    return (
        <div style={{
            borderLeft: `3px solid ${accentColor}`,
            background: '#ffffff',
            borderRadius: '4px',
            padding: '8px 12px',
            marginBottom: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            border: `1px solid #e9ecef`,
            borderLeftColor: accentColor,
            borderLeftWidth: '3px',
        }}>
            {mostrarRotulo && (
                <span style={{
                    display: 'inline-block', fontSize: '10px', fontWeight: 700,
                    color: '#fff', background: accentColor,
                    borderRadius: '3px', padding: '1px 6px', marginBottom: '4px',
                    letterSpacing: '0.4px', textTransform: 'uppercase',
                }}>
                    {tipo}
                </span>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <CopyField text={Formatter.format(valor).replace('R$', '').trim()}>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: accentColor }}>
                        {Formatter.format(valor)}
                    </span>
                </CopyField>
                <CopyField text={moment(data).format('DD/MM/YYYY')} style={{ marginLeft: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                        {moment(data).format('DD/MM/YYYY')}
                    </span>
                </CopyField>
            </div>
            <CopyField text={historico} style={{ display: 'block', margin: '4px 0 6px' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#495057', lineHeight: '1.4', wordBreak: 'break-word' }}>
                    {historico}
                </p>
            </CopyField>
            <div style={{ display: 'flex', gap: '6px' }}>
                {onClick && (
                    <button onClick={() => onClick(id)} type="button"
                        className={`btn btn-sm btn-outline-${isCredito ? 'success' : 'danger'}`}
                        style={{ fontSize: '11px', padding: '1px 10px' }}>
                        Vincular
                    </button>
                )}
                {vinculado && removerClick && (
                    <button type="button" onClick={() => removerClick(id)}
                        className="btn btn-sm btn-outline-secondary"
                        style={{ fontSize: '11px', padding: '1px 10px' }}>
                        Remover
                    </button>
                )}
            </div>
        </div>
    );
}

type UrlParam = {
    cartaoParam: string,
    esteiraParam: string
}

function ConciliacaoInteligente() {

    const [carregado, setCarregado] = useState<boolean>(false);
    const [creditos, setCreditos] = useState<ILancamento[]>([]);
    const [debitos, setDebitos] = useState<ILancamento[]>([]);
    const [contaAtiva, setContaAtiva] = useState<ICartaoDetalhe>();
    const [esteiraAtiva, setEsteiraAtiva] = useState<IEsteiraDetalhe>();
    const [conciliados, setConciliados] = useState<IConciliados>({ creditos: [], debitos: [] });

    // Paginação e loading separados por tipo
    const [isLoadingCredito, setIsLoadingCredito] = useState<boolean>(false);
    const [isLoadingDebito, setIsLoadingDebito] = useState<boolean>(false);
    const [pageCredito, setPageCredito] = useState<number>(1);
    const [totalPagesCredito, setTotalPagesCredito] = useState<number>(1);
    const [totalCredito, setTotalCredito] = useState<number>(0);
    const [pageDebito, setPageDebito] = useState<number>(1);
    const [totalPagesDebito, setTotalPagesDebito] = useState<number>(1);
    const [totalDebito, setTotalDebito] = useState<number>(0);

    type FiltroColuna = { ano: string; mes: string; dia: string; historico: string; valor: string };
    const filtroVazio: FiltroColuna = { ano: '', mes: '', dia: '', historico: '', valor: '' };

    // Filtros independentes por coluna
    const [filtrosCredito, setFiltrosCredito] = useState<FiltroColuna>(filtroVazio);
    const [filtrosDebito, setFiltrosDebito] = useState<FiltroColuna>(filtroVazio);

    // Vinculação em massa (DEVN3-516): "selecionar todos" guarda o filtro ativo no momento do
    // clique + o total já conhecido (totalCredito/totalDebito) — nunca a lista de IDs (podem ser
    // até ~1 milhão), o Worker resolve isso no backend a partir do filtro.
    type SelecaoTodos = { filtro: FiltroColuna; total: number };
    const [creditosSelecionarTodos, setCreditosSelecionarTodos] = useState<SelecaoTodos | null>(null);
    const [debitosSelecionarTodos, setDebitosSelecionarTodos] = useState<SelecaoTodos | null>(null);
    const [vinculandoEmMassa, setVinculandoEmMassa] = useState<boolean>(false);

    const { esteiraParam, cartaoParam } = useParams<UrlParam>();
    const history = useHistory();

    const buildFiltro = useCallback((f: FiltroColuna) => {
        const parseIntSafe = (s: string) => { const n = parseInt(s, 10); return isNaN(n) ? undefined : n; };
        const parseValor = (s: string) => {
            // Remove separadores de milhar (pontos antes de vírgula ou antes de 3 dígitos no final)
            const normalizado = s.replace(/\./g, '').replace(',', '.');
            const n = parseFloat(normalizado);
            return isNaN(n) ? undefined : n;
        };
        return {
            ano: f.ano ? parseIntSafe(f.ano) : undefined,
            mes: f.mes ? parseIntSafe(f.mes) : undefined,
            dia: f.dia ? parseIntSafe(f.dia) : undefined,
            historico: f.historico.trim() || undefined,
            valor: f.valor.trim() ? parseValor(f.valor.trim()) : undefined,
        };
    }, []);

    const carregarCreditos = useCallback((page: number, filtros?: FiltroColuna) => {
        const f = filtros ?? filtrosCredito;
        setIsLoadingCredito(true);
        lancamentoNovaService.listarPendentes(
            parseInt(esteiraParam), parseInt(cartaoParam), page, 20, 'C', buildFiltro(f)
        ).then((resp) => {
            setCreditos(resp.items as unknown as ILancamento[]);
            setTotalPagesCredito(resp.totalPages);
            setTotalCredito(resp.total);
            setPageCredito(page);
        }).finally(() => setIsLoadingCredito(false));
    }, [esteiraParam, cartaoParam, filtrosCredito, buildFiltro]);

    const carregarDebitos = useCallback((page: number, filtros?: FiltroColuna) => {
        const f = filtros ?? filtrosDebito;
        setIsLoadingDebito(true);
        lancamentoNovaService.listarPendentes(
            parseInt(esteiraParam), parseInt(cartaoParam), page, 20, 'D', buildFiltro(f)
        ).then((resp) => {
            setDebitos(resp.items as unknown as ILancamento[]);
            setTotalPagesDebito(resp.totalPages);
            setTotalDebito(resp.total);
            setPageDebito(page);
        }).finally(() => setIsLoadingDebito(false));
    }, [esteiraParam, cartaoParam, filtrosDebito, buildFiltro]);

    const listarTodasContasEsteira = useCallback(() => {
        cartaoNovaService.buscarCartaoPorId(parseInt(cartaoParam) || 0).then((resp) => {
            setContaAtiva(resp);
            setConciliados({ creditos: [], debitos: [] });
            carregarCreditos(1, filtroVazio);
            carregarDebitos(1, filtroVazio);
        });
    }, [cartaoParam, carregarCreditos, carregarDebitos]);

    const listarTodasEsteiras = useCallback(() => {
        esteiraNovaService.buscarDetalhe(parseInt(esteiraParam) || 0).then((resp) => {
            listarTodasContasEsteira();
            setEsteiraAtiva(resp);
        });
    }, [esteiraParam, listarTodasContasEsteira]);

    const adicionarCreditoNaListaDeConciliacao = useCallback((idTributo) => {
        if (!conciliados.creditos.find(i => i.id === idTributo)) {
            const result = creditos.find(i => i.id === idTributo);
            const old = conciliados.creditos || [];
            if (result) {
                setConciliados({ creditos: [...old, result], debitos: conciliados.debitos });
                setCreditos(creditos.filter((item) => item.id !== idTributo));
            }
        }
    }, [conciliados, creditos]);

    const adicionarDebitoNaListaDeConciliacao = useCallback((idTributo) => {
        if (!conciliados.debitos.find(i => i.id === idTributo)) {
            const result = debitos.find(i => i.id === idTributo);
            const old = conciliados.debitos || [];
            if (result) {
                setConciliados({ debitos: [...old, result], creditos: conciliados.creditos });
                setDebitos(debitos.filter((item) => item.id !== idTributo));
            }
        }
    }, [conciliados, debitos]);

    // "Selecionar todos" só habilita enquanto o lado correspondente ainda não tem nenhum
    // lançamento selecionado (manual ou via select-all) — ver regra confirmada na DEVN3-516.
    const ladoCreditoTemSelecao = conciliados.creditos.length > 0 || !!creditosSelecionarTodos;
    const ladoDebitoTemSelecao = conciliados.debitos.length > 0 || !!debitosSelecionarTodos;

    const selecionarTodosCreditos = useCallback(() => {
        setCreditosSelecionarTodos({ filtro: filtrosCredito, total: totalCredito });
    }, [filtrosCredito, totalCredito]);

    const selecionarTodosDebitos = useCallback(() => {
        setDebitosSelecionarTodos({ filtro: filtrosDebito, total: totalDebito });
    }, [filtrosDebito, totalDebito]);

    const cancelarSelecionarTodosCreditos = useCallback(() => {
        setCreditosSelecionarTodos(null);
        carregarCreditos(1);
    }, [carregarCreditos]);

    const cancelarSelecionarTodosDebitos = useCallback(() => {
        setDebitosSelecionarTodos(null);
        carregarDebitos(1);
    }, [carregarDebitos]);

    const [conciliando, setConciliando] = useState<boolean>(false);

    const realizarConciliacao = useCallback(() => {
        const ids: number[] = [];
        conciliados.creditos.forEach((item) => { if (item.id) ids.push(item.id); });
        conciliados.debitos.forEach((item) => { if (item.id) ids.push(item.id); });
        if (ids.length === 0) return;
        setConciliando(true);
        EmpresasService.conciliarLancamentos(ids).then(() => {
            setConciliados({ creditos: [], debitos: [] });
            carregarCreditos(pageCredito);
            carregarDebitos(pageDebito);
        }).catch((err) => {
            const msg = err?.message || err?.response?.data || 'Erro desconhecido';
            alert(`Erro ao conciliar: ${msg}`);
            console.error('[CONCILIACAO] Erro:', msg, 'IDs:', ids);
        }).finally(() => {
            setConciliando(false);
        });
    }, [conciliados, carregarCreditos, carregarDebitos, pageCredito, pageDebito]);

    // Polling do status da Operacao (padrão já usado em mapaResultado/andamentoContas): consulta
    // Esteira/{id}/operacoes a cada 8s até a operação sair de "processando".
    const aguardarConclusaoVinculacaoEmMassa = useCallback((operacaoId: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            const interval = setInterval(async () => {
                try {
                    const operacoes = await EmpresasService.buscarOperacoesEsteira(parseInt(esteiraParam));
                    const operacao = operacoes.find((o: any) => o.id === operacaoId);
                    if (!operacao || operacao.processando) return;
                    clearInterval(interval);
                    if (operacao.erro) reject(new Error('O processamento falhou.'));
                    else resolve();
                } catch {
                    // Falha pontual de polling: tenta de novo no próximo tick.
                }
            }, 8000);
        });
    }, [esteiraParam]);

    const confirmarVinculacaoEmMassa = useCallback(async () => {
        const totalCred = creditosSelecionarTodos ? creditosSelecionarTodos.total : conciliados.creditos.length;
        const totalDeb = debitosSelecionarTodos ? debitosSelecionarTodos.total : conciliados.debitos.length;

        const swalCustom = Swal.mixin({
            customClass: { confirmButton: 'btn btn-success ms-3', cancelButton: 'btn btn-outline-secondary' },
            buttonsStyling: false,
        });

        const result = await swalCustom.fire({
            title: 'Vinculação em massa',
            text: `Isso vai vincular ${totalCred} crédito(s) a ${totalDeb} débito(s) num único grupo de conciliação. Confirmar?`,
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Confirmar',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        setVinculandoEmMassa(true);

        const montarLado = (itens: ILancamento[], selecaoTodos: SelecaoTodos | null) => ({
            ids: itens.map((item) => item.id).filter((id): id is number => !!id),
            selecionarTodos: !!selecaoTodos,
            filtro: selecaoTodos ? buildFiltro(selecaoTodos.filtro) : undefined,
        });

        try {
            const { operacaoId } = await EmpresasService.vincularEmMassa({
                esteiraId: parseInt(esteiraParam),
                cartaoId: parseInt(cartaoParam),
                creditos: montarLado(conciliados.creditos, creditosSelecionarTodos),
                debitos: montarLado(conciliados.debitos, debitosSelecionarTodos),
            });

            await aguardarConclusaoVinculacaoEmMassa(operacaoId);

            Swal.fire({ text: 'Vinculação em massa concluída com sucesso.', timer: 3000, icon: 'success', showConfirmButton: false });
        } catch (err: any) {
            const msg = err?.message || err?.response?.data?.message || 'Erro desconhecido';
            Swal.fire({ text: `Erro na vinculação em massa: ${msg}`, icon: 'error' });
        } finally {
            setConciliados({ creditos: [], debitos: [] });
            setCreditosSelecionarTodos(null);
            setDebitosSelecionarTodos(null);
            setVinculandoEmMassa(false);
            carregarCreditos(1);
            carregarDebitos(1);
        }
    }, [conciliados, creditosSelecionarTodos, debitosSelecionarTodos, esteiraParam, cartaoParam, buildFiltro, carregarCreditos, carregarDebitos, aguardarConclusaoVinculacaoEmMassa]);

    const removerDebitoConciliacao = useCallback((idTributo) => {
        const encontrado = conciliados.debitos.find(item => item.id === idTributo);
        if (encontrado) {
            const old = debitos;
            setDebitos([...old, encontrado]);
            const oldConciliados = conciliados.debitos.filter(item => item.id !== idTributo);
            setConciliados({ debitos: oldConciliados, creditos: conciliados.creditos });
        }
    }, [conciliados, debitos]);

    const removerCreditoConciliacao = useCallback((idTributo) => {
        const encontrado = conciliados.creditos.find(item => item.id === idTributo);
        if (encontrado) {
            const old = creditos;
            setCreditos([...old, encontrado]);
            const oldConciliados = conciliados.creditos.filter(item => item.id !== idTributo);
            setConciliados({ creditos: oldConciliados, debitos: conciliados.debitos });
        }
    }, [conciliados, creditos]);

    useEffect(() => {
        if (!carregado) {
            listarTodasEsteiras();
            setCarregado(true);
        }
    }, [carregado, listarTodasEsteiras]);

    const colHeader = (color: string): CSSProperties => ({
        background: color,
        color: '#fff',
        borderRadius: '6px 6px 0 0',
        padding: '8px 14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 600,
        fontSize: '14px',
    });

    const totalCreditos = conciliados?.creditos?.map((item) => item.valorProvisaoRecolher || 0).reduce((a, b) => a + b, 0) || 0;
    const totalDebitos = conciliados?.debitos?.map((item) => item.valorProvisaoRecolher || 0).reduce((a, b) => a + b, 0) || 0;
    const diferenca = Math.abs(totalDebitos - totalCreditos);

    // Tela de loading até ter conta, esteira e primeira carga de ambas as colunas
    const primeiraCarregaPendente = (isLoadingCredito && creditos.length === 0) || (isLoadingDebito && debitos.length === 0);
    if (!contaAtiva || !esteiraAtiva || primeiraCarregaPendente) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh', gap: '16px' }}>
                <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }} role="status" />
                <span className="text-muted">Carregando lançamentos...</span>
            </div>
        );
    }

    const PaginacaoColuna = ({ page, totalPages, total, onPrev, onNext, loading }: {
        page: number; totalPages: number; total: number;
        onPrev: () => void; onNext: () => void; loading: boolean;
    }) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 2px', borderTop: '1px solid #e9ecef', marginTop: '6px' }}>
            <button onClick={onPrev} disabled={page <= 1 || loading} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>‹ Ant.</button>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : `Pág. ${page} / ${totalPages} (${total} total)`}
            </span>
            <button onClick={onNext} disabled={page >= totalPages || loading} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>Próx. ›</button>
        </div>
    );

    return <>
        {
            carregado ?
                <>
                    {/* Barra de info do topo */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '14px', overflow: 'hidden' }}>
                        {[
                            { label: 'Período', value: `${esteiraAtiva.periodoInicioString} – ${esteiraAtiva.periodoFimString}` },
                            { label: 'Conta', value: `${contaAtiva.contaEmpresa} – ${contaAtiva.descricaoEmpresa}` },
                        ].map((item, idx) => (
                            <div key={idx} style={{ padding: '10px 20px', borderRight: '1px solid #e9ecef', flex: '1 1 auto' }}>
                                <div style={{ fontSize: '10px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: '2px' }}>{item.label}</div>
                                <div style={{ fontWeight: 600, fontSize: '14px', color: '#343a40' }}>{item.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Três colunas */}
                    <div className="row" style={{ marginBottom: '16px' }}>

                        {/* Créditos */}
                        <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={colHeader('#28a745')}>
                                <span>Créditos Pendentes</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button type="button" onClick={selecionarTodosCreditos}
                                        disabled={ladoCreditoTemSelecao || vinculandoEmMassa}
                                        style={{
                                            fontSize: '11px', padding: '1px 8px', borderRadius: '4px',
                                            background: 'rgba(255,255,255,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.6)',
                                        }}>
                                        Selecionar todos
                                    </button>
                                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '1px 10px', fontSize: '12px' }}>
                                        {totalCredito}
                                    </span>
                                </div>
                            </div>
                            {creditosSelecionarTodos ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', minHeight: '260px', padding: '16px', border: '1px solid #28a745', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#f0fff4' }}>
                                    <span style={{ fontSize: '13px', color: '#155724', textAlign: 'center' }}>
                                        Todos os <strong>{creditosSelecionarTodos.total}</strong> créditos pendentes (respeitando o filtro ativo) estão selecionados.
                                    </span>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={vinculandoEmMassa} onClick={cancelarSelecionarTodosCreditos}>
                                        Cancelar seleção
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Filtro da coluna de créditos */}
                                    <div style={{ border: '1px solid #28a745', borderTop: 'none', borderBottom: 'none', padding: '8px 10px', background: '#f0fff4' }}>
                                        <div style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                                            <input className="form-control form-control-sm" type="number" placeholder="Ano" value={filtrosCredito.ano}
                                                onChange={e => { const v = e.target.value; setFiltrosCredito(prev => ({ ...prev, ano: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarCreditos(1); }} style={{ width: '72px' }} />
                                            <input className="form-control form-control-sm" type="number" placeholder="Mês" min={1} max={12} value={filtrosCredito.mes}
                                                onChange={e => { const v = e.target.value; setFiltrosCredito(prev => ({ ...prev, mes: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarCreditos(1); }} style={{ width: '60px' }} />
                                            <input className="form-control form-control-sm" type="number" placeholder="Dia" min={1} max={31} value={filtrosCredito.dia}
                                                onChange={e => { const v = e.target.value; setFiltrosCredito(prev => ({ ...prev, dia: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarCreditos(1); }} style={{ width: '56px' }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <input className="form-control form-control-sm" type="text" placeholder="Histórico..." value={filtrosCredito.historico}
                                                onChange={e => { const v = e.target.value; setFiltrosCredito(prev => ({ ...prev, historico: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarCreditos(1); }} style={{ flex: 1 }} />
                                            <input className="form-control form-control-sm" type="text" placeholder="Valor" value={filtrosCredito.valor}
                                                onChange={e => { const v = e.target.value; setFiltrosCredito(prev => ({ ...prev, valor: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarCreditos(1); }} style={{ width: '76px' }} />
                                            <button className="btn btn-sm btn-success" onClick={() => carregarCreditos(1)} style={{ padding: '2px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}>Buscar</button>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setFiltrosCredito(filtroVazio); carregarCreditos(1, filtroVazio); }} style={{ padding: '2px 8px', fontSize: '12px' }}>×</button>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, maxHeight: '440px', overflowY: 'auto', padding: '10px', border: '1px solid #28a745', borderTop: 'none', background: '#f9fff9' }}>
                                        {isLoadingCredito ? (
                                            <div className="d-flex flex-column align-items-center justify-content-center mt-4" style={{ gap: '12px' }}>
                                                <div className="spinner-border text-success" role="status" />
                                                <span className="text-muted">Carregando...</span>
                                            </div>
                                        ) : (
                                            <>
                                                {creditos.length === 0 && <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>Nenhum crédito encontrado.</p>}
                                                {creditos.map((item) => (
                                                    <CardLancamento mostrarRotulo={false} onClick={(id: number) => adicionarCreditoNaListaDeConciliacao(id)} key={uniqueId()} id={item.id} valor={item.valorProvisaoRecolher || 0} historico={item.historico} vinculado={false} data={item.dataLancamento || new Date()} tipo='Crédito' />
                                                ))}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ border: '1px solid #28a745', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '6px 10px', background: '#f9fff9' }}>
                                        <PaginacaoColuna
                                            page={pageCredito} totalPages={totalPagesCredito} total={totalCredito} loading={isLoadingCredito}
                                            onPrev={() => carregarCreditos(pageCredito - 1)}
                                            onNext={() => carregarCreditos(pageCredito + 1)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Conciliação Indicada */}
                        <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ background: '#6f42c1', color: '#fff', borderRadius: '6px 6px 0 0', padding: '8px 14px', fontWeight: 600, fontSize: '14px' }}>
                                Conciliação Indicada
                            </div>
                            <div style={{ flex: 1, maxHeight: '580px', overflowY: 'auto', padding: '10px', border: '1px solid #6f42c1', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#faf9ff' }}>
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                                    <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: '#d4edda', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '10px', color: '#155724', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Créditos</div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#155724' }}>{creditosSelecionarTodos ? '—' : Formatter.format(totalCreditos)}</div>
                                    </div>
                                    <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: '#fff3cd', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '10px', color: '#856404', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Diferença</div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#856404' }}>{(creditosSelecionarTodos || debitosSelecionarTodos) ? '—' : Formatter.format(diferenca)}</div>
                                    </div>
                                    <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: '#f8d7da', borderRadius: '4px' }}>
                                        <div style={{ fontSize: '10px', color: '#721c24', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Débitos</div>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#721c24' }}>{debitosSelecionarTodos ? '—' : Formatter.format(totalDebitos)}</div>
                                    </div>
                                </div>
                                {creditosSelecionarTodos && (
                                    <div style={{ padding: '8px 10px', marginBottom: '8px', background: '#d4edda', border: '1px dashed #28a745', borderRadius: '4px', fontSize: '12px', color: '#155724', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Todos os {creditosSelecionarTodos.total} créditos (filtro ativo)</span>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={vinculandoEmMassa} onClick={cancelarSelecionarTodosCreditos} style={{ fontSize: '10px', padding: '0px 6px' }}>×</button>
                                    </div>
                                )}
                                {debitosSelecionarTodos && (
                                    <div style={{ padding: '8px 10px', marginBottom: '8px', background: '#f8d7da', border: '1px dashed #dc3545', borderRadius: '4px', fontSize: '12px', color: '#721c24', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Todos os {debitosSelecionarTodos.total} débitos (filtro ativo)</span>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={vinculandoEmMassa} onClick={cancelarSelecionarTodosDebitos} style={{ fontSize: '10px', padding: '0px 6px' }}>×</button>
                                    </div>
                                )}
                                {conciliados?.creditos.map((item) => <CardLancamento mostrarRotulo key={uniqueId()} id={item.id} valor={item.valorProvisaoRecolher || 0} historico={item.historico} tipo='Crédito' vinculado removerClick={() => removerCreditoConciliacao(item.id)} data={item.dataLancamento || new Date()} />)}
                                {conciliados?.debitos.map((item) => <CardLancamento mostrarRotulo key={uniqueId()} id={item.id} valor={item.valorProvisaoRecolher || 0} historico={item.historico} tipo='Débito' vinculado removerClick={() => removerDebitoConciliacao(item.id)} data={item.dataLancamento || new Date()} />)}
                                {(!conciliados || conciliados.creditos.length <= 0 || conciliados.debitos.length <= 0) && (
                                    <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>Nenhum lançamento selecionado.</p>
                                )}
                            </div>
                        </div>

                        {/* Débitos */}
                        <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={colHeader('#dc3545')}>
                                <span>Débitos Pendentes</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button type="button" onClick={selecionarTodosDebitos}
                                        disabled={ladoDebitoTemSelecao || vinculandoEmMassa}
                                        style={{
                                            fontSize: '11px', padding: '1px 8px', borderRadius: '4px',
                                            background: 'rgba(255,255,255,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.6)',
                                        }}>
                                        Selecionar todos
                                    </button>
                                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '1px 10px', fontSize: '12px' }}>
                                        {totalDebito}
                                    </span>
                                </div>
                            </div>
                            {debitosSelecionarTodos ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', minHeight: '260px', padding: '16px', border: '1px solid #dc3545', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#fff5f5' }}>
                                    <span style={{ fontSize: '13px', color: '#721c24', textAlign: 'center' }}>
                                        Todos os <strong>{debitosSelecionarTodos.total}</strong> débitos pendentes (respeitando o filtro ativo) estão selecionados.
                                    </span>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={vinculandoEmMassa} onClick={cancelarSelecionarTodosDebitos}>
                                        Cancelar seleção
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Filtro da coluna de débitos */}
                                    <div style={{ border: '1px solid #dc3545', borderTop: 'none', borderBottom: 'none', padding: '8px 10px', background: '#fff5f5' }}>
                                        <div style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                                            <input className="form-control form-control-sm" type="number" placeholder="Ano" value={filtrosDebito.ano}
                                                onChange={e => { const v = e.target.value; setFiltrosDebito(prev => ({ ...prev, ano: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarDebitos(1); }} style={{ width: '72px' }} />
                                            <input className="form-control form-control-sm" type="number" placeholder="Mês" min={1} max={12} value={filtrosDebito.mes}
                                                onChange={e => { const v = e.target.value; setFiltrosDebito(prev => ({ ...prev, mes: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarDebitos(1); }} style={{ width: '60px' }} />
                                            <input className="form-control form-control-sm" type="number" placeholder="Dia" min={1} max={31} value={filtrosDebito.dia}
                                                onChange={e => { const v = e.target.value; setFiltrosDebito(prev => ({ ...prev, dia: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarDebitos(1); }} style={{ width: '56px' }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <input className="form-control form-control-sm" type="text" placeholder="Histórico..." value={filtrosDebito.historico}
                                                onChange={e => { const v = e.target.value; setFiltrosDebito(prev => ({ ...prev, historico: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarDebitos(1); }} style={{ flex: 1 }} />
                                            <input className="form-control form-control-sm" type="text" placeholder="Valor" value={filtrosDebito.valor}
                                                onChange={e => { const v = e.target.value; setFiltrosDebito(prev => ({ ...prev, valor: v })); }}
                                                onKeyDown={e => { if (e.key === 'Enter') carregarDebitos(1); }} style={{ width: '76px' }} />
                                            <button className="btn btn-sm btn-danger" onClick={() => carregarDebitos(1)} style={{ padding: '2px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}>Buscar</button>
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setFiltrosDebito(filtroVazio); carregarDebitos(1, filtroVazio); }} style={{ padding: '2px 8px', fontSize: '12px' }}>×</button>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, maxHeight: '440px', overflowY: 'auto', padding: '10px', border: '1px solid #dc3545', borderTop: 'none', background: '#fff9f9' }}>
                                        {isLoadingDebito ? (
                                            <div className="d-flex flex-column align-items-center justify-content-center mt-4" style={{ gap: '12px' }}>
                                                <div className="spinner-border text-danger" role="status" />
                                                <span className="text-muted">Carregando...</span>
                                            </div>
                                        ) : (
                                            <>
                                                {debitos.length === 0 && <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>Nenhum débito encontrado.</p>}
                                                {debitos.map((item) => (
                                                    <CardLancamento mostrarRotulo={false} onClick={(id: number) => adicionarDebitoNaListaDeConciliacao(id)} key={uniqueId()} id={item.id} valor={item.valorProvisaoRecolher || 0} historico={item.historico} vinculado={false} tipo='Débito' data={item.dataLancamento || new Date()} />
                                                ))}
                                            </>
                                        )}
                                    </div>
                                    <div style={{ border: '1px solid #dc3545', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '6px 10px', background: '#fff9f9' }}>
                                        <PaginacaoColuna
                                            page={pageDebito} totalPages={totalPagesDebito} total={totalDebito} loading={isLoadingDebito}
                                            onPrev={() => carregarDebitos(pageDebito - 1)}
                                            onNext={() => carregarDebitos(pageDebito + 1)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Ações */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', paddingBottom: '24px' }}>
                        {ladoCreditoTemSelecao && ladoDebitoTemSelecao && !creditosSelecionarTodos && !debitosSelecionarTodos && (
                            <button onClick={() => realizarConciliacao()} className="btn btn-success" type="button" disabled={conciliando}
                                style={{ padding: '8px 28px', fontWeight: 600, borderRadius: '6px', fontSize: '15px' }}>
                                {conciliando ? 'Conciliando...' : 'Conciliar'}
                            </button>
                        )}
                        {ladoCreditoTemSelecao && ladoDebitoTemSelecao && (creditosSelecionarTodos || debitosSelecionarTodos) && (
                            <button onClick={() => confirmarVinculacaoEmMassa()} className="btn btn-success" type="button" disabled={vinculandoEmMassa}
                                style={{ padding: '8px 28px', fontWeight: 600, borderRadius: '6px', fontSize: '15px' }}>
                                {vinculandoEmMassa ? 'Processando...' : 'Vincular em massa'}
                            </button>
                        )}
                        <button type="button" className="btn btn-outline-secondary" disabled={vinculandoEmMassa} onClick={async () => {
                            try {
                                history.goBack();
                            } catch (err: any) {
                                const msg = err?.message || err?.response?.data || 'Erro desconhecido';
                                alert(`Erro ao concluir conciliação: ${msg}`);
                                console.error('[CONCLUIR] Erro:', msg);
                            }
                        }} style={{ padding: '8px 24px', fontWeight: 600, borderRadius: '6px' }}>
                            Concluir Conciliação
                        </button>
                    </div>
                </>
                : <p className="mt-5">Carregando...</p>
        }
    </>
};

export default ConciliacaoInteligente;